import { queryClient } from "api/queryClient";
import { Suspense } from "react";
import { createBrowserRouter } from "react-router";
import ErrorPage from "../ErrorPage/ErrorPage";
import RootLayout from "../Layout/RootLayout";
import NotFoundPage from "../NotFoundPage";
import PreloadPage from "../PreloadPage/PreloadPage";
import PrivateRoute from "../PrivateRoute/PrivateRoute";
import { privateRoutes, publicRoutes, type RouteItem } from "./appRoutes";

const defaultFallback = <PreloadPage isLoading />;

// Map private routes
const mappedPrivateRoutes = privateRoutes.map((route: RouteItem) => {
	const Component = route.component;
	return {
		path: route.path,
		handle: { title: route.title },
		errorElement: <ErrorPage />,
		element: Component ? (
			<Suspense fallback={route.fallback || defaultFallback}>
				<PrivateRoute component={Component} />
			</Suspense>
		) : null,
		loader: route.onLoad
			? async ({ params }: { params: Record<string, string | undefined> }) => {
					await route.onLoad?.(queryClient, params);
					return null;
				}
			: undefined,
	};
});

// Map public routes
const mappedPublicRoutes = publicRoutes.map((route: RouteItem) => {
	const Component = route.component;
	return {
		path: route.path,
		handle: { title: route.title },
		errorElement: <ErrorPage />,
		element: Component ? (
			<Suspense fallback={route.fallback || defaultFallback}>
				<Component />
			</Suspense>
		) : null,
		loader: route.onLoad
			? async ({ params }: { params: Record<string, string | undefined> }) => {
					await route.onLoad?.(queryClient, params);
					return null;
				}
			: undefined,
	};
});

export const router = createBrowserRouter([
	{
		element: <RootLayout />,
		// Catches anything the layout itself throws, and anything a route throws
		// before its own errorElement can take over.
		errorElement: <ErrorPage />,
		children: [
			...mappedPrivateRoutes,
			...mappedPublicRoutes,
			{
				path: "*",
				element: <NotFoundPage />,
			},
		],
	},
]);
